import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { useGetPromoDesignLibrary } from '../../../queries/promos';
import { ar } from './promosLogic';
import { SHAPE_SIZE } from './kit/constants';
import { checkImageSize } from './kit/sizeCheck';

/**
 * Library picker, shared by the list and the editor.
 *  - `single`  = choose exactly one image (the product photo): no size rule.
 *  - `shape`   = when choosing DESIGNS for a shape that needs an exact size (bar 1920x216, L 1920x1080, corner 400x400), an image that
 *                does not fit (checkImageSize, the same verdict the upload tab shows) is badged «مقاس غير مناسب», cannot be selected and is
 *                listed after the ones that fit. The natural size comes from the thumbnail's own onLoad, so no extra request is made.
 * The header and the footer button live OUTSIDE the scrolling content (PrimeReact header/footer), so they are always visible.
 * Styled with the promo tokens only (promos.css `.promos-dlg`): nothing here uses the legacy dialog/card colours.
 */
export default function MediaPickerDialog({ visible, onHide, onPickMany, existingIds, t, single = false, shape = null }) {
  const { data, isLoading } = useGetPromoDesignLibrary(visible);
  const images = useMemo(() => {
    const arr = data?.data ?? data ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [data]);
  const need = !single && shape ? SHAPE_SIZE[shape] || null : null;

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sizes, setSizes] = useState({});
  // Fresh selection every time the dialog opens — a leftover checkmark from a previous, already-confirmed session would be confusing.
  useEffect(() => {
    if (visible) setSelectedIds(new Set());
  }, [visible]);

  const setSize = (id, img) => setSizes((x) => (x[id] ? x : { ...x, [id]: { w: img.naturalWidth || 0, h: img.naturalHeight || 0 } }));
  // 'ok' | 'bad' | 'pending'. Unknown / unreadable sizes count as ok (checkImageSize), pending ones stay selectable-looking until read.
  const verdictOf = (m) => {
    if (!need) return 'ok';
    const s = sizes[m.id];
    if (!s) return 'pending';
    return checkImageSize(shape, s.w, s.h).ok ? 'ok' : 'bad';
  };
  const rows = useMemo(
    () => images
      .map((m, i) => ({ m, i, v: verdictOf(m) }))
      .sort((a, b) => (a.v === 'bad') - (b.v === 'bad') || a.i - b.i),
    [images, sizes, need, shape] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const toggle = (m, v) => {
    if (v === 'bad' || existingIds.has(m.id)) return;
    setSelectedIds((prev) => {
      if (single) return new Set(prev.has(m.id) ? [] : [m.id]);
      const next = new Set(prev);
      if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
      return next;
    });
  };

  const confirm = () => {
    const chosen = images.filter((m) => selectedIds.has(m.id) && verdictOf(m) !== 'bad');
    if (chosen.length === 0) return;
    onPickMany(chosen.map((m) => ({ id: m.id, file_name: m.file_name, creative_url: m.path })));
    onHide();
  };

  const n = selectedIds.size;
  const header = (
    <div className="pk-h">
      <b>{t('promos.picker_title')}</b>
      {need && <small>{t('promos.v2_pk_need', { w: ar(need.w), h: ar(need.h) })}</small>}
    </div>
  );
  const footer = (
    <button type="button" className="pk-add" disabled={n === 0} onClick={confirm}>
      {n > 0 ? t('promos.picker_add_n', { n: ar(n) }) : t('promos.picker_add_none')}
    </button>
  );

  return (
    <Dialog
      className="promos-dlg" header={header} footer={footer} visible={visible} onHide={onHide}
      style={{ width: 'min(96vw, 640px)' }} modal draggable={false} resizable={false} blockScroll
    >
      {isLoading ? (
        <div className="pk-grid">{[...Array(6)].map((_, i) => <Skeleton key={i} height="7rem" borderRadius="12px" />)}</div>
      ) : images.length === 0 ? (
        <div className="pk-empty">{t('promos.picker_empty')}</div>
      ) : (
        <div className="pk-grid">
          {rows.map(({ m, v }) => {
            const already = existingIds.has(m.id);
            const bad = v === 'bad';
            const selected = selectedIds.has(m.id);
            const s = sizes[m.id];
            return (
              <button
                key={m.id} type="button"
                className={`pk-item${selected ? ' on' : ''}${bad ? ' bad' : ''}${already ? ' done' : ''}`}
                disabled={already || bad} aria-pressed={selected}
                onClick={() => toggle(m, v)}
              >
                <span className="pk-thumb">
                  <img
                    src={m.path} alt="" loading="lazy"
                    onLoad={(e) => setSize(m.id, e.currentTarget)}
                    onError={() => setSizes((x) => (x[m.id] ? x : { ...x, [m.id]: { w: 0, h: 0 } }))}
                  />
                  {selected && <i className="pk-ck" aria-hidden="true">✓</i>}
                  {already && <span className="pk-tag">{t('promos.picker_already_added')}</span>}
                  {bad && <span className="pk-tag bad">{t('promos.v2_pk_bad')}</span>}
                </span>
                <span className="pk-dim">{s && s.w ? `${ar(s.w)}×${ar(s.h)}` : ' '}</span>
              </button>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
